import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { MatchRepository } from "@/modules/leagues/repositories/MatchRepository";
import type { LeagueRepository } from "@/modules/leagues/repositories/LeagueRepository";
import type { Match, ParticipantPrediction } from "@/modules/leagues/types/match";
import { footballDataService } from "@/services/football/FootballDataService";
import { FootballApiError } from "@/services/football/FootballApiClient";
import {
  loadUserPredictions,
  applyPredictionsAndClubFilter,
  getParticipantPredictionsForMatch,
  predictionDocId,
} from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";

/**
 * Implementação REAL de MatchRepository (Football Data API + Firestore),
 * usada quando USE_MOCK_DATA = false. Nenhuma tela conhece esta classe
 * diretamente — ela só existe atrás da interface MatchRepository.
 *
 * As PARTIDAS vêm da Football Data API (nunca inventadas). Os PALPITES do
 * usuário logado são persistidos de verdade no Firestore, coleção raiz
 * `predictions`, doc id `${leagueId}_${matchId}_${uid}` (upsert idempotente,
 * evita duplicar palpite pra mesma partida). Ver Priority 1 do escopo
 * "bolão funcional de verdade".
 */
export class FootballDataMatchRepository implements MatchRepository {
  constructor(private readonly leagues: LeagueRepository) {}

  private async resolveLeague(leagueId: string) {
    const league = await this.leagues.getLeagueById(leagueId);
    if (!league) throw new FootballApiError("not_found", "Liga não encontrada.");
    return league;
  }

  private requireUid(): string {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Você precisa estar autenticado para palpitar.");
    return uid;
  }

  /**
   * Descobre a temporada certa pra pedir à API pra essa competição. Tenta
   * primeiro a temporada salva na liga (rápido, sem chamada extra); se a
   * API não retornar NENHUM jogo com ela, verifica com a football-data.org
   * qual é a temporada que ELA considera atual pra essa competição
   * (necessário pra competições europeias, cuja temporada é rotulada pelo
   * ano de início — ex: Champions League 2025/26 = season=2025, não 2026 —
   * ver getApiCurrentSeasonYear). Nunca inventa partida: só corrige o
   * parâmetro de busca antes de tentar de novo.
   */
  private async fetchMatchesWithSeasonFallback(
    competitionId: string,
    leagueId: string,
    storedSeason: number,
    matchday: number | undefined,
    stage: string | null = null
  ): Promise<Match[]> {
    const firstTry = await footballDataService.getMatches(competitionId, leagueId, storedSeason, matchday, stage);
    if (firstTry.length > 0) return firstTry;

    const apiSeason = await footballDataService.getApiCurrentSeasonYear(competitionId);
    if (apiSeason === null || apiSeason === storedSeason) return firstTry; // nada mais a tentar

    console.warn(
      `[FootballDataMatchRepository] competição ${competitionId}: temporada salva na liga (${storedSeason}) não retornou jogos; tentando temporada real da API (${apiSeason}).`
    );
    return footballDataService.getMatches(competitionId, leagueId, apiSeason, matchday, stage);
  }

  async getByRound(leagueId: string, round?: number): Promise<Match[]> {
    const league = await this.resolveLeague(leagueId);
    let matchday = round;
    let stage: string | null = null;
    if (matchday === undefined) {
      const current = await footballDataService.getCurrentRoundInfo(league.competitionId, league.season);
      matchday = current?.round ?? undefined;
      stage = current?.stage ?? null;
    }
    const [matches, predictions] = await Promise.all([
      this.fetchMatchesWithSeasonFallback(league.competitionId, leagueId, league.season, matchday, stage),
      loadUserPredictions(leagueId),
    ]);
    return applyPredictionsAndClubFilter(matches, predictions, league.disabledClubIds, league.rules.rules);
  }

  async getById(leagueId: string, matchId: string): Promise<Match | null> {
    const league = await this.resolveLeague(leagueId);
    const match = await footballDataService.getMatch(matchId, leagueId);
    if (!match) return null;
    const predictions = await loadUserPredictions(leagueId);
    const [result] = applyPredictionsAndClubFilter([match], predictions, league.disabledClubIds, league.rules.rules);
    return result ?? null;
  }

  async savePrediction(leagueId: string, matchId: string, homeScore: number, awayScore: number): Promise<void> {
    const uid = this.requireUid();
    const league = await this.resolveLeague(leagueId);
    const match = await footballDataService.getMatch(matchId, leagueId);
    if (!match) throw new FootballApiError("not_found", "Partida não encontrada.");
    // Dois critérios, o mais restritivo vale: status da API OU horário real
    // de início já passou (ver applyPredictionsAndClubFilter — mesmo motivo:
    // status da API pode estar desatualizado por causa do cache).
    if (match.status !== "scheduled" || new Date(match.kickoff).getTime() <= Date.now()) {
      throw new Error("Os palpites para esta partida já estão encerrados.");
    }

    const docId = predictionDocId(leagueId, matchId, uid);
    const ref = doc(db, "predictions", docId);
    const existing = await getDoc(ref);
    if (existing.exists() && (existing.data().pointsEarned as number | null) !== null) {
      // Já foi pontuado pelo cron — as Firestore Rules bloqueiam essa edição
      // mesmo (ver firestore.rules), mas falhamos cedo com mensagem clara
      // em vez de deixar o Firestore devolver "permission-denied" cru.
      throw new Error("Este palpite já foi pontuado e não pode mais ser alterado.");
    }
    const now = new Date().toISOString();
    const createdAt = existing.exists() ? (existing.data().createdAt as string) ?? now : now;

    await setDoc(
      ref,
      {
        userId: uid,
        leagueId,
        matchId,
        homeTeam: match.homeTeam.name,
        awayTeam: match.awayTeam.name,
        homeScore,
        awayScore,
        // Timestamp NATIVO (não string) — necessário pra Firestore Rules
        // conseguirem comparar "a partida já começou?" com `request.time`
        // de forma confiável (ver firestore.rules, regra de predictions).
        matchDate: Timestamp.fromDate(new Date(match.kickoff)),
        competition: league.competitionId,
        season: league.season,
        round: match.round,
        // Sempre nulo aqui — só o cron server-side (Admin SDK) preenche
        // isto de verdade. Precisa estar explícito porque a Firestore Rule
        // de create/update exige `pointsEarned == null` (Priority 12).
        pointsEarned: null,
        // Sempre false na criação — só o cron (Admin SDK) liga isso pra
        // true quando a partida começa. Correção definitiva do bug
        // "ninguém palpitou": antes a regra comparava horário na hora da
        // leitura, o que quebrava consultas em lista; agora é um campo
        // fixo, gravado de propósito.
        participantsVisible: false,
        createdAt,
        updatedAt: now,
      },
      { merge: true }
    );
  }

  async getParticipantPredictions(leagueId: string, matchId: string): Promise<ParticipantPrediction[]> {
    const [league, match] = await Promise.all([this.resolveLeague(leagueId), footballDataService.getMatch(matchId, leagueId)]);
    if (!match) return [];
    return getParticipantPredictionsForMatch(leagueId, match, league);
  }

  async getAvailableRounds(leagueId: string): Promise<Array<{ round: number; label: string }>> {
    const league = await this.resolveLeague(leagueId);
    try {
      const rounds = await footballDataService.getSeasonRounds(league.competitionId, league.season);
      if (rounds.length > 0) return rounds;
    } catch {
      // Se a busca da temporada inteira falhar por algum motivo, cai pro
      // fallback abaixo (janela em torno da rodada atual) em vez de quebrar
      // a navegação de rodadas inteira.
    }
    const current = (await footballDataService.getCurrentRoundInfo(league.competitionId, league.season))?.round ?? 1;
    const start = Math.max(1, current - 2);
    const end = current + 3;
    const rounds = [];
    for (let r = start; r <= end; r++) {
      rounds.push({ round: r, label: `Rodada ${r}` });
    }
    return rounds;
  }
}
